import React from 'react';
import Pannello from './Pannello';
import MappaGruppi from './MappaGruppi';
import { perQuota, puntiMappa, contaStoriche, persone } from './analisi';

/**
 * Come sono distribuite le iscrizioni: da dove vengono, quanto pagano, che eta' hanno, e quante
 * squadre sono alla prima volta.
 *
 * Sei riquadri su una griglia fissa di quattro colonne per quattro righe, a celle quadrate: la
 * mappa ne occupa tre per tre, l'elenco dei gruppi le sta accanto in verticale, e la riga in
 * fondo porta eta' e sesso insieme - due celle, perche' un grafico a barre doppie vuole
 * larghezza - poi quote e squadre.
 *
 * La geometria e' fissa e il contenuto ci si adatta: chi non ci sta - l'elenco dei gruppi, che
 * di gruppi ne ha un centinaio - scorre dentro la sua cella invece di allungare la griglia.
 * Su schermo stretto le celle si srotolano in una colonna sola.
 *
 * Le barre sono div con una larghezza in percentuale, non una libreria di grafici: qui servono
 * istogrammi orizzontali, e un <code>div</code> li disegna senza portarsi dietro un sistema di
 * assi e un contenitore che in jsdom non sa quanto e' largo.
 */

/**
 * Una riga della piramide delle eta': l'eta' in mezzo, i maschi che crescono verso sinistra e le
 * femmine verso destra, sulla stessa scala.
 *
 * E' la forma dei grafici di anagrafica, e non e' un vezzo: le due serie condividono l'asse
 * centrale, quindi lo squilibrio fra i due sessi in una fascia si legge senza confrontare due
 * lunghezze lontane.
 */
const RigaPiramide = ({ fascia, maschi, femmine, massimo }) => {
  const larghezza = (valore) => `${massimo > 0 ? (valore / massimo) * 100 : 0}%`;

  return (
    <div className="piramide-riga">
      <span className="piramide-lato piramide-sinistra" title={`${maschi} maschi di ${fascia} anni`}>
        <span className="piramide-corsia">
          <span className="piramide-riempimento riempimento-maschi" style={{ width: larghezza(maschi) }} />
        </span>
        <span className="piramide-numero">{maschi || ''}</span>
      </span>

      <span className="piramide-fascia">{fascia}</span>

      <span className="piramide-lato" title={`${femmine} femmine di ${fascia} anni`}>
        <span className="piramide-corsia">
          <span className="piramide-riempimento riempimento-femmine" style={{ width: larghezza(femmine) }} />
        </span>
        <span className="piramide-numero">{femmine || ''}</span>
      </span>
    </div>
  );
};

/** Una percentuale intera, con lo zero al posto della divisione per zero. */
const percentuale = (parte, totale) => (totale > 0 ? Math.round((parte / totale) * 100) : 0);

/** Una barra orizzontale con l'etichetta a sinistra e il numero a destra. */
const Barra = ({ etichetta, valore, massimo, titolo, suffisso }) => (
  <div className="barra" title={titolo}>
    <span className="barra-etichetta">{etichetta}</span>
    <span className="barra-corsia">
      <span
        className="barra-riempimento"
        style={{ width: `${massimo > 0 ? Math.round((valore / massimo) * 100) : 0}%` }}
      />
    </span>
    <span className="barra-valore">{valore}{suffisso}</span>
  </div>
);

const PannelloDistribuzioni = ({ squadre, gruppi, eta, aperto = false }) => {
  const punti = puntiMappa(gruppi);
  const quote = perQuota(squadre);
  const storiche = contaStoriche(squadre);

  const totalePersone = squadre.reduce((acc, s) => acc + persone(s), 0);
  const incasso = quote.reduce((acc, q) => acc + q.incasso, 0);
  const massimoGruppo = Math.max(...gruppi.map(g => g.giocatori), 0);
  const massimoQuota = Math.max(...quote.map(q => q.squadre), 0);
  const massimoSesso = Math.max(...eta.flatMap(f => [f.maschi, f.femmine]), 0);
  const maschi = eta.reduce((acc, f) => acc + f.maschi, 0);
  const femmine = eta.reduce((acc, f) => acc + f.femmine, 0);
  const massimoStorica = Math.max(storiche.nuova, storiche.ritorno, storiche.nonAgganciata, 0);

  return (
    <Pannello
      aperto={aperto}
      icona="fa-chart-column"
      titolo="Distribuzioni"
      sommario={`${gruppi.length} gruppi scout - ${totalePersone} persone - ${incasso} euro di quote`}
    >
      {squadre.length === 0 ? (
        <p className="pannello-vuoto">Nessuna squadra iscritta.</p>
      ) : (
        <div className="distribuzioni">
          <div className="riquadro cella-mappa">
            <div className="riquadro-titolo">Da dove vengono</div>
            <div className="riquadro-corpo corpo-mappa">
              <MappaGruppi punti={punti} />
              {punti.length < gruppi.length && (
                <p className="pannello-vuoto">
                  {gruppi.length - punti.length} gruppi non sono sulla mappa: nome fuori
                  anagrafica o coordinate illeggibili.
                </p>
              )}
            </div>
          </div>

          <div className="riquadro cella-gruppi">
            <div className="riquadro-titolo">
              Gruppi scout ({gruppi.length})
              <span className="riquadro-nota" title="Gli accompagnatori non dichiarano un gruppo">
                per persona
              </span>
            </div>
            <div className="riquadro-corpo">
              {gruppi.map(gruppo => (
                <Barra
                  key={gruppo.nome}
                  etichetta={gruppo.nome}
                  valore={gruppo.giocatori}
                  massimo={massimoGruppo}
                  titolo={`${gruppo.giocatori} giocatori, in ${gruppo.squadre} squadre`}
                />
              ))}
            </div>
          </div>

          <div className="riquadro cella-eta">
            <div className="riquadro-titolo">
              Eta' e sesso, al giorno del torneo
              <span className="legenda">
                <span className="legenda-voce"><i className="pallino pallino-maschi"></i>maschi a sinistra</span>
                <span className="legenda-voce"><i className="pallino pallino-femmine"></i>femmine a destra</span>
              </span>
            </div>
            <div className="riquadro-corpo">
              {eta.map(f => (
                <RigaPiramide
                  key={f.fascia}
                  fascia={f.fascia}
                  maschi={f.maschi}
                  femmine={f.femmine}
                  massimo={massimoSesso}
                />
              ))}
              <p className="pannello-vuoto">
                {maschi} maschi e {femmine} femmine, {percentuale(femmine, maschi + femmine)}%
                femmine. Gli accompagnatori non ci sono: di loro sappiamo solo quanti sono.
              </p>
            </div>
          </div>

          <div className="riquadro cella-quote">
            <div className="riquadro-titolo">Quote</div>
            <div className="riquadro-corpo">
              <table className="tabella-compatta">
                <thead>
                  <tr>
                    <th>Quota</th>
                    <th>Squadre</th>
                    <th>Gioc.</th>
                    <th>Incasso</th>
                  </tr>
                </thead>
                <tbody>
                  {quote.map(q => (
                    <tr key={q.quota}>
                      <td>{q.quota} euro</td>
                      <td>{q.squadre}</td>
                      <td>{q.giocatori}</td>
                      <td>{q.incasso} euro</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>Totale</td>
                    <td>{squadre.length}</td>
                    <td>{quote.reduce((acc, q) => acc + q.giocatori, 0)}</td>
                    <td>{incasso} euro</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div className="riquadro cella-squadre">
            <div className="riquadro-titolo">Squadre</div>
            <div className="riquadro-corpo">
              <Barra etichetta="Di ritorno" valore={storiche.ritorno} massimo={massimoStorica} suffisso=" sq." />
              <Barra etichetta="Nuove" valore={storiche.nuova} massimo={massimoStorica} suffisso=" sq." />
              <Barra
                etichetta="Non agganciate"
                valore={storiche.nonAgganciata}
                massimo={massimoStorica}
                suffisso=" sq."
                titolo="Iscrizioni senza squadra storica: non si sa se sono nuove o no"
              />
              {storiche.nonAgganciata > 0 && (
                <p className="pannello-vuoto">
                  Le non agganciate non sono squadre nuove: sono iscrizioni che nessuno ha ancora
                  collegato alla loro storica, quindi la loro storia non si puo' leggere.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </Pannello>
  );
};

export default PannelloDistribuzioni;
